// ==========================================================================
// PeekopMark — shared brand mark / logotype
// ==========================================================================
//
// Single source of truth for the Peekop logo across the app. Replaces six
// near-duplicate Tailwind-drawn marks that used to live in landing.tsx,
// faq.tsx, onboarding.tsx, use-cases.tsx, vs-docusign.tsx, plus inline
// versions in shell.tsx and sign-in.tsx. Keeping them in sync across a
// rebrand was a footgun; now one file controls the whole surface.
//
// The actual art lives in /public/brand/ as SVG (vector, crisp on retina)
// with PNG fallbacks. We render via <img src="..."> rather than inlining
// the SVG so the browser can cache it once across every page.

export interface PeekopMarkProps {
  /**
   * "full" renders the mark + "Peekop" wordmark (aspect ~4:1).
   * "icon" renders just the mark (aspect 1:1), for tight spots like the
   * sidebar rail, auth card, or narrow mobile headers.
   */
  variant?: "full" | "icon";
  /**
   * "light" is the default black+teal ink for light backgrounds.
   * "dark" is white+teal ink for dark-background sections (final CTA,
   * future dark mode).
   */
  theme?: "light" | "dark";
  /**
   * Tailwind height class. Width is `w-auto` so the aspect ratio is
   * preserved from the SVG intrinsic dims. Default `h-10` (~40px tall)
   * which is the right weight for marketing navbars and footer; the
   * logotype reads as "brand", not as a micro-chip. Tight in-app
   * chrome (sidebar rail, mobile header, onboarding) should pass
   * `h-8` explicitly.
   */
  className?: string;
}

export function PeekopMark({
  variant = "full",
  theme = "light",
  className = "h-10",
}: PeekopMarkProps) {
  // ?v= busts browser + CDN caches whenever we retune the mark. Bump
  // when the SVG geometry changes.
  const LOCKUP_VERSION = "5";
  const base =
    variant === "icon"
      ? theme === "dark"
        ? "/brand/peekop-mark-white.svg"
        : "/brand/peekop-mark.svg"
      : theme === "dark"
        ? "/brand/peekop-logo-dark.svg"
        : "/brand/peekop-logo.svg";
  const src = `${base}?v=${LOCKUP_VERSION}`;

  // Intrinsic dimensions prevent CLS while the SVG loads. The browser
  // reserves the correct box before paint, so headers don't jump.
  const width = variant === "icon" ? 48 : 200;
  const height = 48;

  return (
    <img
      src={src}
      alt="Peekop"
      width={width}
      height={height}
      className={`w-auto ${className}`}
    />
  );
}
